// The whiskey bottle (heal lane, sim/whiskey.ts): the prop in the cowboy's hand while he chugs, and the empty he
// tosses away, which tumbles, hits the ground and shatters. Purely cosmetic and per-client, as in Godot
// (scripts/vfx/stim_bottle_vfx.gd): every machine sees the same chug from the sim and throws its own copy of the
// bottle, so nothing here is on the wire. The shatter itself (glass, splash, stain, sound) is the host's hook.
//
// One shared lathe geometry for every bottle. Glass: amber, see-through at the edges, a hard sky streak down one
// side and a cream paper label. The whiskey is a second, inset lathe that clips at its fill level, so the level
// drops in swallows as he drinks.

import * as THREE from 'three';
import { HAZE } from '../materials';
import { PAL } from '../palette';

/** the lathe below is drawn 0.25 m tall and stretched to a proper long-necked fifth */
const STRETCH = 1.2;
/** bottle height (m) */
export const BOTTLE_H = 0.25 * STRETCH;
const NECK_TIP = new THREE.Vector3(0, BOTTLE_H, 0);

// (radius, height) up the outside: punted base, straight body, a round shoulder, a long neck, a rolled lip
const PROFILE: [number, number][] = [
  [0, 0.004], [0.03, 0.0], [0.039, 0.003], [0.041, 0.012], [0.041, 0.13], [0.039, 0.145], [0.033, 0.16],
  [0.024, 0.173], [0.017, 0.185], [0.0135, 0.2], [0.0128, 0.232], [0.0158, 0.236], [0.0162, 0.246], [0.0118, 0.25],
];
const LIQ_TOP = 0.178; // the whiskey's level when the bottle is full (just into the shoulder)

let shared: { glass: THREE.LatheGeometry; liquid: THREE.LatheGeometry; glassMat: THREE.ShaderMaterial; liqMat: THREE.ShaderMaterial; ink: THREE.MeshBasicMaterial } | null = null;

const GLASS_VS = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying float vY; varying vec3 vW;
void main() {
  vY = position.y;
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vW = wp.xyz;
  vN = normalize( mat3( modelMatrix ) * normal );
  vV = normalize( cameraPosition - wp.xyz );
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;
const GLASS_FS = /* glsl */ `
uniform vec3 uAmber; uniform vec3 uDeep; uniform vec3 uLabel; uniform vec3 uInk; uniform vec3 uSun; uniform vec3 uSky;
uniform vec3 uHaze; uniform float uHazeK;
varying vec3 vN; varying vec3 vV; varying float vY; varying vec3 vW;
void main() {
  vec3 n = normalize( vN );
  if ( !gl_FrontFacing ) n = -n;
  float nv = clamp( dot( n, normalize( vV ) ), 0.0, 1.0 );
  float lit = dot( n, normalize( uSun ) );
  // glass: deep in the middle, brighter amber where you look through more of it at the rim
  float rim = pow( 1.0 - nv, 2.0 );
  vec3 col = mix( uDeep, uAmber, 0.35 + 0.65 * rim );
  col *= lit > 0.1 ? 1.0 : 0.72; // two hard bands like everything else
  float a = 0.62 + 0.3 * rim;
  // the paper label: cream, with two ink rules and the brand's band
  float lab = step( 0.045, vY ) * step( vY, 0.112 );
  if ( lab > 0.5 ) {
    float rule = step( abs( vY - 0.052 ), 0.0022 ) + step( abs( vY - 0.105 ), 0.0022 );
    float band = step( abs( vY - 0.0785 ), 0.0105 );
    col = mix( uLabel, uInk, clamp( rule, 0.0, 1.0 ) );
    col = mix( col, uInk, band * 0.12 ); // the brand: a faint band, not a stripe
    col *= lit > 0.1 ? 1.0 : 0.8;
    a = 1.0;
  }
  // a hard streak of sky down one side of the glass (the cel highlight)
  vec3 h = normalize( normalize( uSun ) + normalize( vV ) );
  float spec = step( 0.93, dot( n, h ) );
  col = mix( col, uSky, spec * ( 1.0 - lab ) );
  a = max( a, spec * 0.95 );
  float d = length( vW - cameraPosition );
  col = mix( col, uHaze, clamp( d * uHazeK, 0.0, 0.6 ) );
  gl_FragColor = vec4( col, a );
}`;
const LIQ_FS = /* glsl */ `
uniform vec3 uCol; uniform vec3 uSun; uniform float uFill;
varying vec3 vN; varying vec3 vV; varying float vY; varying vec3 vW;
void main() {
  if ( vY > uFill ) discard;
  vec3 n = normalize( vN );
  float lit = dot( gl_FrontFacing ? n : -n, normalize( uSun ) );
  vec3 col = uCol * ( lit > 0.1 ? 1.0 : 0.7 );
  // a bright meniscus line at the surface
  col = mix( col, uCol * 1.7, step( uFill - 0.004, vY ) );
  gl_FragColor = vec4( col, 1.0 );
}`;

function build() {
  if (shared) return shared;
  const pts = PROFILE.map(([r, y]) => new THREE.Vector2(r, y));
  const glass = new THREE.LatheGeometry(pts, 14);
  glass.computeVertexNormals();
  const lpts = PROFILE.filter(([, y]) => y <= LIQ_TOP + 0.02).map(([r, y]) => new THREE.Vector2(Math.max(0, r - 0.004), Math.max(0.006, y)));
  const liquid = new THREE.LatheGeometry(lpts, 12);
  liquid.computeVertexNormals();
  const glassMat = new THREE.ShaderMaterial({
    vertexShader: GLASS_VS, fragmentShader: GLASS_FS, transparent: true, depthWrite: false, side: THREE.DoubleSide,
    uniforms: {
      uAmber: { value: new THREE.Color(0xd98a2b) }, uDeep: { value: new THREE.Color(0x6b3510) },
      uLabel: { value: new THREE.Color(PAL.warmCream) }, uInk: { value: new THREE.Color(PAL.inkDark) },
      // the scene's sun and haze (shared uniforms, so day to night follows)
      uSun: HAZE.uSunDir, uSky: { value: new THREE.Color(0xfff2d6) },
      uHaze: HAZE.uHazeColor, uHazeK: { value: 0.004 },
    },
  });
  const liqMat = new THREE.ShaderMaterial({
    vertexShader: GLASS_VS, fragmentShader: LIQ_FS,
    uniforms: { uCol: { value: new THREE.Color(0x9a4f12) }, uSun: HAZE.uSunDir, uFill: { value: LIQ_TOP } },
  });
  const ink = new THREE.MeshBasicMaterial({ color: PAL.inkDark, side: THREE.BackSide });
  ink.onBeforeCompile = (s) => {
    s.vertexShader = s.vertexShader.replace('#include <begin_vertex>', 'vec3 transformed = position + normalize( normal ) * 0.0045;');
  };
  shared = { glass, liquid, glassMat, liqMat, ink };
  return shared;
}

/** one bottle: glass + whiskey + ink edge. The whiskey material is per bottle (its fill level). */
export class Bottle {
  readonly group = new THREE.Group();
  private liq: THREE.ShaderMaterial;
  constructor() {
    const s = build();
    this.liq = s.liqMat.clone();
    this.liq.uniforms.uSun = HAZE.uSunDir; // clone copies uniforms; keep the shared sun
    const liquid = new THREE.Mesh(s.liquid, this.liq);
    const glass = new THREE.Mesh(s.glass, s.glassMat);
    const ink = new THREE.Mesh(s.glass, s.ink);
    liquid.renderOrder = 0; glass.renderOrder = 2;
    for (const m of [liquid, glass, ink]) { m.frustumCulled = false; m.scale.y = STRETCH; this.group.add(m); }
    this.group.visible = false;
  }
  /** 1 full .. 0 drained (never bone dry) */
  fill(f: number) { this.liq.uniforms.uFill.value = 0.006 + (LIQ_TOP - 0.006) * Math.max(0, Math.min(1, f)); }
  /** the open neck, in world space */
  tip(out: THREE.Vector3) { return out.copy(NECK_TIP).applyMatrix4(this.group.matrixWorld); }
  dispose() { this.liq.dispose(); }
}

/** the swallows: Godot's gulp_sequence. Four quick drops across the head-back hold, each settling back a hair. */
export function gulpFill(t: number, from: number, to: number, gulps = 4) {
  if (t <= from) return 1;
  if (t >= to) return 0.08;
  const k = ((t - from) / (to - from)) * gulps, i = Math.floor(k), f = k - i;
  const lvl = (j: number) => 1 - (j / gulps) * 0.92;
  // a fast swallow over the first 45 % of each gulp, a slosh back up, then still
  const s = f < 0.45 ? Math.sin((f / 0.45) * Math.PI * 0.5) : 1;
  const slosh = f >= 0.45 && f < 0.65 ? Math.sin(((f - 0.45) / 0.2) * Math.PI) * 0.035 : 0;
  return lvl(i) + (lvl(i + 1) - lvl(i)) * s + slosh;
}

interface Flying { b: Bottle; v: THREE.Vector3; w: THREE.Vector3; spin: number; age: number }

/**
 * Bottles in flight: tossed out of a hand, tumbling under gravity until they meet the ground, where `onShatter`
 * fires (the game's FX and sound) and the bottle goes back to the pool.
 */
export class Tosses {
  readonly root = new THREE.Group();
  private live: Flying[] = [];
  private pool: Bottle[] = [];
  onShatter: ((x: number, y: number, z: number, vx: number, vy: number, vz: number) => void) | null = null;
  constructor(private ground: (x: number, z: number, y: number) => number) { this.root.name = 'whiskey-tosses'; }

  /** throw an empty from `m` (the bottle's world matrix as it leaves the hand) with velocity v */
  toss(m: THREE.Matrix4, v: THREE.Vector3, spinAxis: THREE.Vector3, spin: number) {
    if (this.live.length >= 12) return;
    const b = this.pool.pop() ?? new Bottle();
    if (!b.group.parent) this.root.add(b.group);
    m.decompose(b.group.position, b.group.quaternion, b.group.scale);
    b.fill(0.08);
    b.group.visible = true;
    this.live.push({ b, v: v.clone(), w: spinAxis.clone().normalize(), spin, age: 0 });
  }

  update(dt: number) {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const f = this.live[i], g = f.b.group;
      f.age += dt;
      f.v.y -= 13 * dt; // a touch heavier than real gravity: the arc reads snappy at game scale
      g.position.addScaledVector(f.v, dt);
      _q.setFromAxisAngle(f.w, f.spin * dt);
      g.quaternion.premultiply(_q);
      g.updateMatrixWorld(true);
      // the lowest point of the tumbling bottle (base or neck) meets the ground
      _a.set(0, 0, 0).applyMatrix4(g.matrixWorld); _b.copy(NECK_TIP).applyMatrix4(g.matrixWorld);
      const low = Math.min(_a.y, _b.y);
      const gy = this.ground(g.position.x, g.position.z, g.position.y + 0.5);
      if (low <= gy + 0.01 || f.age > 3 || gy < -1e6 && f.age > 1.2) {
        if (gy > -1e6) this.onShatter?.(g.position.x, gy, g.position.z, f.v.x, f.v.y, f.v.z);
        g.visible = false;
        this.pool.push(f.b);
        this.live.splice(i, 1);
      }
    }
  }
  dispose() { for (const f of this.live) f.b.dispose(); for (const b of this.pool) b.dispose(); this.live.length = 0; this.pool.length = 0; this.root.removeFromParent(); }
}
const _q = new THREE.Quaternion(), _a = new THREE.Vector3(), _b = new THREE.Vector3();
